import React, { Fragment, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { toast } from 'sonner';
import { ChevronRightIcon, LockIcon, PlusIcon } from 'lucide-react';
import { ruleTypeOrder } from '../../data/compPlan';
import { AuditEntry, CompRule, PlanVersion } from '../../types/commission';
import { formatRuleBound, formatRuleValue } from '../../utils/commission';
import { Badge } from '../sf/Badge';
import { Button } from '../sf/Button';
import { NewBadge } from '../sf/NewBadge';
import { Td, Th } from '../sf/DataTable';
import { ScopeBadge } from '../sf/ScopeBadge';
import { Tooltip } from '../sf/Tooltip';
import { EditableCell } from './EditableCell';
import { EditableField, cellKey, useRulesTable } from './useRulesTable';
interface RulesTableProps {
  version: PlanVersion;
  editable: boolean;
  readOnlyReason: string;
  editor: string;
  onAudit: (entries: AuditEntry[]) => void;
}
const COLUMN_COUNT = 10;
export function RulesTable({
  version,
  editable,
  readOnlyReason,
  editor,
  onAudit
}: RulesTableProps) {
  const table = useRulesTable(version.rules, version.version, editor);
  const [reason, setReason] = useState('');
  const [reasonMissing, setReasonMissing] = useState(false);
  function handleSave() {
    if (!reason.trim()) {
      setReasonMissing(true);
      return;
    }
    const count = table.dirtyCount;
    const entries = table.save(reason.trim());
    onAudit(entries);
    setReason('');
    setReasonMissing(false);
    toast.success(`Saved ${count} rule ${count === 1 ? 'change' : 'changes'} to v${version.version} Draft`, {
      description: `${entries.length} field ${entries.length === 1 ? 'change' : 'changes'} written to the audit log.`
    });
  }
  function handleCancel() {
    table.cancel();
    setReason('');
    setReasonMissing(false);
  }
  const renderValue = (rule: CompRule) => rule.value === null && rule.sourceNote ? <span className="whitespace-nowrap text-xs text-sf-weak" data-id="element-140" data-mp="1rnviqet7n4t2">{rule.sourceNote}</span> : rule.value === null ? <span className="inline-flex items-center justify-end gap-1.5 whitespace-nowrap" data-id="element-141" data-mp="3tbi0dmhd89v8">
        <Badge tone="warning" data-id="element-142" data-mp="3oe7z99km69rk">Pending confirmation</Badge>
        <span className="font-semibold text-sf-amber-text" data-id="element-143" data-mp="3jlyh4fpqqdzu">{formatRuleValue(rule)}</span>
      </span> : formatRuleValue(rule);
  const renderEditable = (rule: CompRule, field: EditableField, display: React.ReactNode) => {
    const key = cellKey(rule.id, field);
    const cellEditable = editable && (field === 'value' || rule.basis !== 'none');
    return <Td align="right" className={`${table.editedCells.has(key) ? 'bg-sf-edited' : ''} ${cellEditable ? 'py-1' : ''}`} data-id="element-144" data-mp="35orxyk13uda3">
        <EditableCell display={display} rawValue={rule[field]} label={`${field} for ${rule.ruleKey}`} editable={cellEditable} isEditing={table.editingCell === key} onStart={() => table.setEditingCell(key)} onCommit={raw => table.commitCell(rule.id, field, raw)} data-id="element-145" data-mp="2wm0kyfskz9gc" />
      </Td>;
  };
  return <div className="overflow-hidden rounded border border-sf-border" data-id="element-146" data-mp="2cq6ant20biq9">
      <div className="overflow-x-auto" data-id="element-147" data-mp="2hpiyoh4m6vuf">
        <table className="w-full border-collapse" data-id="element-148" data-mp="2mhsmnrm65lu2">
          <thead data-id="element-149" data-mp="2mic313w6825z">
            <tr data-id="element-150" data-mp="2mjwxyumedn4d">
              <Th data-id="element-151" data-mp="2qje47q9neoal">Rule Key</Th>
              <Th data-id="element-152" data-mp="2qjniiglbz70g">Scope</Th>
              <Th data-id="element-153" data-mp="2qivym313ixq0">Branch / Comp Type</Th>
              <Th data-id="element-154" data-mp="1tt4tzaz3bu6h">Employee</Th>
              <Th data-id="element-155" data-mp="1ttwqqp4fbxyc">Applies To</Th>
              <Th align="right" data-id="element-156" data-mp="1tupr98cshr81">Min</Th>
              <Th align="right" data-id="element-157" data-mp="1yry7v4iahn76">Max</Th>
              <Th align="right" data-id="element-158" data-mp="1yt0xza7sflm1">
                <span className="inline-flex items-center gap-1" data-id="element-159" data-mp="1yuc3begvt1f9">
                  {!editable && <LockIcon className="h-3 w-3" aria-label="Read-only" data-id="element-160" data-mp="22s9tokme1tk8" />}
                  Value
                </span>
              </Th>
              <Th data-id="element-161" data-mp="3e901u9adgvgh">Unit</Th>
              <Th data-id="element-162" data-mp="3e9rkkjiveypc">Last Modified</Th>
            </tr>
          </thead>
          <tbody data-id="element-163" data-mp="3j82ueu96197k">
            {ruleTypeOrder.map(type => {
            const rows = table.rules.filter(rule => rule.ruleType === type);
            const isCollapsed = table.collapsed.has(type);
            return <Fragment key={type} data-id="element-164" data-mp="3csmazk1vxue2">
                  <tr data-id="element-165" data-mp="3ctnjphvfi4sm">
                    <td colSpan={COLUMN_COUNT} className="border-b border-sf-border bg-[#FAFAF9] px-2 py-1.5" data-id="element-166" data-mp="3qrlwwjy0jv6j">
                      <div className="flex items-center justify-between gap-3" data-id="element-167" data-mp="3mq8oq37g7fwg">
                        <button type="button" onClick={() => table.toggleGroup(type)} aria-expanded={!isCollapsed} className="flex items-center gap-1.5 rounded px-1 py-0.5 text-[13px] font-semibold text-sf-text transition-colors duration-150 ease-out hover:bg-sf-bg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sf-blue" data-id="element-168" data-mp="3msd3jg6vxzbi">
                          <ChevronRightIcon className={`h-4 w-4 text-sf-weak transition-transform duration-150 ease-out ${isCollapsed ? '' : 'rotate-90'}`} aria-hidden="true" data-id="element-169" data-mp="nb52uoqn5orj" />
                          {type}
                          <span className="font-normal text-sf-weak" data-id="element-170" data-mp="11vhk0cb83pkc">({rows.length})</span>
                        </button>
                        {editable ? <Button size="sm" icon={<PlusIcon data-id="element-172" data-mp="swmel2g06ji3" />} onClick={() => table.addRule(type)} data-id="element-171" data-mp="svuk5uy56rte">
                            Add Rule
                          </Button> : <Tooltip content={readOnlyReason} align="end" data-id="element-173" data-mp="j6ay6gf4uhub">
                            <Button size="sm" icon={<LockIcon data-id="element-175" data-mp="nyukc1rvylaq" />} disabled data-id="element-174" data-mp="nyb5wv1g2f1g">
                              Add Rule
                            </Button>
                          </Tooltip>}
                      </div>
                    </td>
                  </tr>
                  {!isCollapsed && rows.length === 0 && <tr data-id="element-176" data-mp="90brwelniwa">
                      <td colSpan={COLUMN_COUNT} className="border-b border-sf-border px-3 py-2 pl-9 text-xs text-sf-weak" data-id="element-177" data-mp="akxqxmultij">
                        {version.groupNotes?.[type] ?? 'No rules in this version.'}
                      </td>
                    </tr>}
                  {!isCollapsed && rows.map(rule => <tr key={rule.id} data-id="element-178" data-mp="1x7h5qfvjo9k7">
                        <Td className="whitespace-nowrap pl-9 font-mono text-xs text-sf-text" data-id="element-179" data-mp="1t52lpsg533rc">{rule.ruleKey}</Td>
                        <Td data-id="element-180" data-mp="2pv35fd1niu1s">
                          <ScopeBadge scope={rule.scope} data-id="element-181" data-mp="2pua9z8857qyy" />
                        </Td>
                        <Td className="whitespace-nowrap text-sf-weak" data-id="element-182" data-mp="2kw0boor4ga94">{rule.branch ?? rule.compType ?? '—'}</Td>
                        <Td className="whitespace-nowrap text-sf-weak" data-id="element-183" data-mp="2gtlvydir1qm2">{rule.employee ?? '—'}</Td>
                        <Td className="min-w-[180px] text-xs text-sf-weak" data-id="element-184" data-mp="2buipqpr19eci">
                          {rule.proposed && <span className="mb-1 block" data-id="element-185" data-mp="15gt472pyl260">
                              <NewBadge label="NEW · v2 proposed, not in main" data-id="element-186" data-mp="10g5f4mlaivef" />
                            </span>}
                          {rule.description}
                        </Td>
                        {renderEditable(rule, 'min', formatRuleBound(rule, 'min'))}
                        {renderEditable(rule, 'max', formatRuleBound(rule, 'max'))}
                        {renderEditable(rule, 'value', renderValue(rule))}
                        <Td className="text-sf-weak" data-id="element-187" data-mp="3pwzxkf1olvor">{rule.unit}</Td>
                        <Td className="whitespace-nowrap text-xs text-sf-weak" data-id="element-188" data-mp="3utqcgz1r2l8l">
                          {rule.lastModifiedOn ? <>
                              <span className="text-sf-text" data-id="element-189" data-mp="8mi922ae4of7">{rule.lastModifiedBy}</span> · {rule.lastModifiedOn}
                            </> : rule.id.startsWith('new-') ? <span className="italic" data-id="element-190" data-mp="2af6k2xta0g5j">Unsaved</span> : '—'}
                        </Td>
                      </tr>)}
                </Fragment>;
          })}
            <tr data-id="element-191" data-mp="1ql6jet8iwv48">
              <td colSpan={COLUMN_COUNT} className="border-b border-sf-border bg-sf-amber-light px-3 py-1.5 text-[13px] font-semibold text-sf-amber-text" data-id="element-192" data-mp="1qjlpn5u19g7f">
                Requested — not applied
              </td>
            </tr>
            {version.pending.map(item => <tr key={item.ruleKey} data-id="element-193" data-mp="1ckm4471thhfh">
                <Td className="whitespace-nowrap pl-9 font-mono text-xs" data-id="element-194" data-mp="1clotubneldtt">{item.ruleKey}</Td>
                <Td colSpan={4} className="text-[13px]" data-id="element-195" data-mp="17n40mw6kjuev">
                  <span className="font-semibold" data-id="element-196" data-mp="1llbtot0hibkr">{item.label}</span>
                  <span className="block text-xs text-sf-weak" data-id="element-197" data-mp="1ljh6f81qq7di">{item.detail}</span>
                </Td>
                <Td colSpan={5} align="right" data-id="element-198" data-mp="1golgcdg2du31">
                  <span className="inline-flex items-center gap-1.5" data-id="element-199" data-mp="2wzxpdbci7654">
                    <NewBadge label="NEW / requested" data-id="element-200" data-mp="2s1nu70qs45ji" />
                    <Badge tone="neutral" data-id="element-201" data-mp="2s14bhhtb1d4f">Not applied</Badge>
                  </span>
                </Td>
              </tr>)}
          </tbody>
        </table>
      </div>
      {table.error && <p className="border-t border-sf-border bg-sf-red-light px-4 py-2 text-xs text-sf-red" data-id="element-202" data-mp="34lt7h7jmzqdz">{table.error}</p>}
      <AnimatePresence initial={false} data-id="element-203" data-mp="2qmbeefzb5qyt">
        {editable && table.dirtyCount > 0 && <motion.div initial={{
        opacity: 0,
        y: 8
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: 8
      }} transition={{
        duration: 0.2,
        ease: [0.23, 1, 0.32, 1]
      }} className="flex flex-wrap items-center gap-3 border-t border-sf-border bg-sf-bg px-4 py-2.5" data-id="element-204" data-mp="2vk3fo0luepfo">
            <p className="text-[13px] text-sf-text" data-id="element-205" data-mp="39hi71avnp7aa">
              <span className="mr-1.5 inline-block h-2.5 w-2.5 rounded-sm border border-[#E0E39A] bg-sf-edited align-middle" aria-hidden="true" data-id="element-206" data-mp="3eh4m7pv1oxlq" />
              {table.dirtyCount} unsaved {table.dirtyCount === 1 ? 'rule' : 'rules'}
            </p>
            <div className="ml-auto flex items-start gap-2" data-id="element-207" data-mp="czxlilg6bcu5">
              <div data-id="element-208" data-mp="d0gq70gtc4q8">
                <label htmlFor={`reason-${version.version}`} className="sr-only" data-id="element-209" data-mp="cxuin0tx9p80">
                  Reason for change (required)
                </label>
                <input id={`reason-${version.version}`} value={reason} onChange={event => {
              setReason(event.target.value);
              if (event.target.value.trim()) setReasonMissing(false);
            }} placeholder="Reason for change (required)" aria-invalid={reasonMissing} className={`h-8 w-[300px] rounded border bg-white px-3 text-[13px] focus:outline-none focus:ring-1 ${reasonMissing ? 'border-sf-red focus:ring-sf-red' : 'border-sf-border-strong focus:border-sf-blue focus:ring-sf-blue'}`} data-id="element-210" data-mp="3y7i0h90ong0" />
                {reasonMissing && <p className="mt-1 text-xs text-sf-red" data-id="element-211" data-mp="3cej3qi7znnh4">Add a reason — it’s saved to the audit log.</p>}
              </div>
              <Button onClick={handleCancel} data-id="element-212" data-mp="2svd7wywdvpmk">Cancel</Button>
              <Button variant="brand" onClick={handleSave} data-id="element-213" data-mp="2nv8zzx9jk37y">
                Save
              </Button>
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}